/**
 * The pure half of the GPU/CPU parity check: how far apart two renders of the same mockup are.
 *
 * Kept out of `parity.ts` (which needs a document, a canvas and a 2D context) so the arithmetic that
 * decides pass or fail can be tested in node, where the rest of that file cannot run at all.
 */

/**
 * Mean absolute error between two top-down RGBA images over the pixels `keep` selects, in 0..1
 * units — `|a − b| / 255` averaged over the R, G and B of every counted pixel. Alpha is ignored:
 * both images are opaque composites.
 *
 * The one loop both selections below are measured with, so "inside the print areas, on the garment"
 * and "the backdrop region" can never drift into two different notions of an average.
 *
 * Returns 0 when nothing is counted: two images that overlap nowhere have no disagreement to report,
 * and it is the caller (which knows whether a frame was rendered at all) that decides if that is a
 * failure.
 */
export function meanAbsErrorWhere(
  a: Uint8ClampedArray,
  b: Uint8ClampedArray,
  keep: (x: number, y: number) => boolean,
  width: number,
  height: number,
): number {
  let sum = 0, n = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!keep(x, y)) continue;
      const p = (y * width + x) * 4;
      sum += Math.abs(a[p] - b[p]) + Math.abs(a[p + 1] - b[p + 1]) + Math.abs(a[p + 2] - b[p + 2]);
      n++;
    }
  }
  return n === 0 ? 0 : sum / (n * 3 * 255);
}

/** How many pixels `meanAbsErrorWhere` would count for the same selection — the count every caller
 *  checks before trusting a 0 (see `comparedPixels`). */
export function countedPixels(keep: (x: number, y: number) => boolean, width: number, height: number): number {
  let n = 0;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) if (keep(x, y)) n++;
  return n;
}

/**
 * The pixels the two renderers are asked to agree about for the PRINT: both on the garment
 * (`mask > 0`) and inside `inside`. Off the mask the compositor leaves the photo's own backdrop
 * alone (or paints the job's, which `offMask` below measures instead), and outside the print areas
 * neither renderer puts ink.
 */
export function onPrint(
  mask: Uint8Array,
  inside: (x: number, y: number) => boolean,
  width: number,
): (x: number, y: number) => boolean {
  return (x, y) => mask[y * width + x] > 0 && inside(x, y);
}

/**
 * The pixels the BACKDROP owns: `mask === 0` exactly — no garment at all, so nothing there is the
 * photo's own garment showing through a feathered edge.
 *
 * `=== 0` rather than "not solid": a mask value between 1 and 254 is the feather, where the CPU
 * mixes the backdrop with the garment and the GPU's `mix` does the same — those pixels are already
 * counted by `onPrint` wherever they fall inside a print area, and counting them here as well would
 * average two different comparisons into one number. What this selects is the region a backdrop swap
 * is FOR: the pixels that are pure backdrop, which `meanAbsError` over the print areas never sees.
 */
export function offMask(mask: Uint8Array, width: number): (x: number, y: number) => boolean {
  return (x, y) => mask[y * width + x] === 0;
}

/**
 * Mean absolute error over the pixels both on the garment and inside the print areas — the print
 * comparison, in the shape every caller and test was written against.
 */
export function meanAbsError(
  a: Uint8ClampedArray,
  b: Uint8ClampedArray,
  mask: Uint8Array,
  inside: (x: number, y: number) => boolean,
  width: number,
  height: number,
): number {
  return meanAbsErrorWhere(a, b, onPrint(mask, inside, width), width, height);
}

/**
 * How many pixels `meanAbsError` would count for the same mask and area test.
 *
 * It exists so the caller can tell a genuine agreement from a comparison that never happened:
 * `meanAbsError` returns 0 for an empty count (it has nothing to report), and a parity harness that
 * handed that 0 back as a pass would be exactly the thing it was built to prevent. Separate from
 * `meanAbsError` rather than folded into its return, so that function keeps the one-number signature
 * every caller and its own tests are written against.
 */
export function comparedPixels(
  mask: Uint8Array,
  inside: (x: number, y: number) => boolean,
  width: number,
  height: number,
): number {
  return countedPixels(onPrint(mask, inside, width), width, height);
}
